import React from 'react';
import _ from 'underscore';
import {
  TableColumnDefinition,
  createTableColumn,
} from '@fluentui/react-components';
import { EntityName } from '@bingads-webui-campaign-react/overview-grid-entity-name';

import { IColumnData, IMetricFormatter } from './types';
import { TextCell } from './table-cell/text-cell';
import { ChangeBarCell } from './table-cell/change-bar-cell';
import { InsightCell } from './table-cell/insight-cell';
import { PopoverInfo } from './popover-info';
import { EntityNameColumnId, ChangeBarColumnId, InsightColumnId } from './const';

interface IGetColumnsProps {
  selectedMetric: string;
  metricFormatter: IMetricFormatter;
  currentDateRange: {
    end: Date;
    start: Date;
  };
  comparisonDateRange: {
    end: Date;
    start: Date;
  };
  i18n: any;
  renderFluctuationPopup: Function;
  metrics: Array<{
    key: string,
    displayName: string,
  }>;
}

const FluctuationTypeMap = {
  Clicks: 'Click',
  Impressions: 'Impression',
  Spend: 'Spend',
  Conversions: 'Conversion',
};

function hasFluctuation(availableInsights, selectedMetric) {
  const current = FluctuationTypeMap[selectedMetric];
  return current && _.contains(availableInsights, current);
}

export function getColumns({
  selectedMetric,
  metricFormatter,
  currentDateRange,
  comparisonDateRange,
  i18n,
  metrics,
  renderFluctuationPopup,
}: IGetColumnsProps): TableColumnDefinition<IColumnData>[] {
  const localizedMetricName = metrics?.find(m => m?.key === selectedMetric)?.displayName;
  return [
    createTableColumn<IColumnData>({
      columnId: EntityNameColumnId,
      renderCell: (item) => {
        let popoverContent = null;
        if (item.getPopoverContent) {
          popoverContent = item.getPopoverContent(item);
        }
        return (<EntityName
          entityType={item.entityType}
          href={item.href}
          name={item.Name}
          status={item.Status}
          isExperiment={!!item.Campaign?.ExperimentId || !!item.ExperimentId}
          popoverContent={popoverContent}
          className="entity-name-cell"
        />);
      },
    }),
    createTableColumn<IColumnData>({
      columnId: ChangeBarColumnId,
      renderCell: item => (
        <PopoverInfo
          i18n={i18n}
          change={item.PerformanceMetricsChange[selectedMetric]}
          percentageChange={item.PerformanceMetricsChangePercentage[selectedMetric]}
          metricName={selectedMetric}
          currentMetric={item.PerformanceMetrics[selectedMetric]}
          periodMetric={item.PeriodPerformanceMetrics[selectedMetric]}
          currentDateRange={currentDateRange}
          comparisonDateRange={comparisonDateRange}
          entityName={item.Name}
          localizedMetricName={localizedMetricName}
          metricFormatter={metricFormatter}
          currency={item.Currency}
        >
          <ChangeBarCell change={item.PerformanceMetricsChange[selectedMetric]} maxChange={item.maxChange} />
        </PopoverInfo >
      ),
    }),
    createTableColumn<IColumnData>({
      columnId: InsightColumnId,
      renderCell: (item) => {
        if (hasFluctuation(item.AvailableInsightTypeIds, selectedMetric)) {
          const fluctuationTypeIds = _.filter(item.AvailableInsightTypeIds, MTMetric => MTMetric === FluctuationTypeMap[selectedMetric]);
          const metric = {
            name: localizedMetricName,
            value: metricFormatter.formatMetricWithSign(selectedMetric, item.PerformanceMetricsChange[selectedMetric], {
              currency: item.Currency,
            }),
          };
          return (
            <InsightCell fluctuationTypeIds={fluctuationTypeIds} i18n={i18n} renderFluctuationPopup={renderFluctuationPopup} entity={item} metric={metric} />
          );
        }
        return (<></>);
      },
    }),
    createTableColumn<IColumnData>({
      columnId: `${selectedMetric}-change-value`,
      renderCell: (item) => {
        const value = metricFormatter.formatMetricWithSign(selectedMetric, item.PerformanceMetricsChange[selectedMetric], {
          currency: item.Currency,
        });
        return (
          <PopoverInfo
            i18n={i18n}
            change={item.PerformanceMetricsChange[selectedMetric]}
            percentageChange={item.PerformanceMetricsChangePercentage[selectedMetric]}
            metricName={selectedMetric}
            currentMetric={item.PerformanceMetrics[selectedMetric]}
            periodMetric={item.PeriodPerformanceMetrics[selectedMetric]}
            currentDateRange={currentDateRange}
            comparisonDateRange={comparisonDateRange}
            entityName={item.Name}
            localizedMetricName={localizedMetricName}
            metricFormatter={metricFormatter}
            currency={item.Currency}
          >
            <TextCell value={value} cellName="value" />
          </PopoverInfo>
        );
      },
    }),
    createTableColumn<IColumnData>({
      columnId: `${selectedMetric}-change-percentage`,
      renderCell: (item) => {
        const value = metricFormatter.formatChangePercentageWithSign(item.PerformanceMetricsChangePercentage[selectedMetric]);
        return (
          <PopoverInfo
            i18n={i18n}
            change={item.PerformanceMetricsChange[selectedMetric]}
            percentageChange={item.PerformanceMetricsChangePercentage[selectedMetric]}
            metricName={selectedMetric}
            currentMetric={item.PerformanceMetrics[selectedMetric]}
            periodMetric={item.PeriodPerformanceMetrics[selectedMetric]}
            currentDateRange={currentDateRange}
            comparisonDateRange={comparisonDateRange}
            entityName={item.Name}
            localizedMetricName={localizedMetricName}
            metricFormatter={metricFormatter}
            currency={item.Currency}
          >
            <TextCell value={value} cellName="percentage" />
          </PopoverInfo>
        );
      },
    }),
  ];
}
